/**
 * Deep links universales del backend (`data.deep_link`).
 *
 * El backend manda un path canónico y sin rol:
 *
 *   /chat/{id}          conversación
 *   /notifications      pantalla de Notificaciones
 *   /settings/sessions  Sesiones activas
 *   /packs/{id}         detalle/edición del pack
 *   /profile/photos     fotos de perfil/portada
 *   /coins              monedero / recargas
 *
 * Acá se traduce a la ruta local de la web (que sí lleva el prefijo de rol).
 * Es el ÚNICO lugar que conoce esa diferencia: tanto el handler de push de
 * OneSignal como la lista de notificaciones pasan por `webPathFromDeepLink`.
 *
 * Roles: `model`, `client`, `user`, `admin`, `assistant`. Una cuenta todavía
 * sin rol (login con Google antes de elegir) no tiene dashboard: cualquier deep
 * link la manda a `/choose-role`, igual que hace RouteCheck.
 */

// Roles con dashboard propio. `user` comparte el shell de cliente pero tiene su
// propio prefijo de ruta y pantallas.
const ROLE_PREFIXES = {
  model: 'model',
  client: 'client',
  user: 'user',
  admin: 'admin',
  assistant: 'assistant',
};

const CHOOSE_ROLE_PATH = '/choose-role';

/** Saca el pathname de una URL o path; null si no es del mismo origen. */
const toPathname = (url) => {
  if (!url || typeof url !== 'string') return null;
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.origin !== window.location.origin) return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
};

/**
 * Traduce un `deep_link` (o cualquier path canónico) a la ruta local de la web
 * para `role`. Devuelve:
 *   - la ruta local, si el rol y el path son reconocidos;
 *   - `/choose-role`, si el usuario todavía no eligió rol;
 *   - null, si no se puede resolver (el llamador decide el fallback).
 */
export const webPathFromDeepLink = (deepLink, role) => {
  const path = toPathname(deepLink);
  if (!path) return null;

  // Sin rol: no puede entrar a ninguna pantalla privada todavía.
  if (!role) return CHOOSE_ROLE_PATH;

  const prefix = ROLE_PREFIXES[role];
  // Rol desconocido: que decida el llamador (no inventar un prefijo).
  if (!prefix) return null;

  if (path.startsWith('/chat/')) {
    return `/${prefix}/chat/${path.slice('/chat/'.length)}`;
  }
  if (path === '/notifications') {
    return `/${prefix}/notifications`;
  }
  if (path === '/settings/sessions') {
    return `/${prefix}/sessions`;
  }

  // Pack concreto. Cada rol tiene su pantalla: la modelo lo edita, el cliente y
  // `user` lo compran. Admin/assistant no tienen detalle de pack → null (el
  // llamador cae a Notificaciones).
  if (path.startsWith('/packs/')) {
    const packId = path.slice('/packs/'.length);
    if (!packId) return null;
    if (role === 'model') return `/${prefix}/packs/edit/${packId}`;
    if (role === 'client' || role === 'user') return `/${prefix}/buyPack/${packId}`;
    return null;
  }

  // Fotos de perfil/portada: hoy solo la modelo tiene esa pantalla.
  if (path === '/profile/photos') {
    return role === 'model' ? `/${prefix}/profile` : null;
  }

  // Monedero/recargas: hoy solo el cliente.
  if (path === '/coins') {
    return role === 'client' ? `/${prefix}/rechargeCoins` : null;
  }

  return null;
};

export default webPathFromDeepLink;
